/**
 * InvoiceListPage 發票 (S7.1) — Board D #7, 會計 Finance nav.
 *
 * Rebuilt out of the codex `FinancePages.tsx` onto the house list stack
 * (useListPage + GenericFilterPopover + TableContainer state triad). Tabs are
 * ROUTE-DERIVED (`/auth/finance/invoices/:kind`, kind = customer|vendor) —
 * selection lives in the URL, never in component state.
 *
 * - Customer invoices 客戶發票 are GENERATED from loaded qty only (invoice-on-
 *   loaded, 已裝量為唯一開票依據): the generate modal prefills the invoiceable
 *   draft (`GET /finance/customer-invoices/draft`) — loaded − invoiced per
 *   fulfillment line, container 櫃號 suggested as the invoice number — and the
 *   server rejects any line above that ceiling (INVOICE_ABOVE_LOADED).
 * - Vendor invoices 供應商發票 are MANUAL intake (未整合 MVP): type the
 *   faxed/emailed invoice against a PO; three-way match rows derive
 *   automatically.
 * - When a draft has NOTHING invoiceable the submit action is hidden with an
 *   explanation (hide-don't-disable).
 *
 * Slice-local seam (S7.1 file-set): the table hook + api calls are co-located
 * here because `src/hooks/**` / `src/services/api/**` / `src/constants/
 * api.constants.ts` belong to concurrent slices this wave — follow-up: lift
 * into `useInvoices.ts` + `invoice.api.ts` when those files free up.
 */

import { useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  App,
  Badge,
  Button,
  Col,
  DatePicker,
  Dropdown,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Table,
  Tabs,
  Tag,
  Typography,
  theme,
} from 'antd';
import type { TableProps } from 'antd';
import {
  EyeOutlined,
  FileTextOutlined,
  FilterOutlined,
  MoreOutlined,
  PlusOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import dayjs from 'dayjs';
import { useAntdTable, useRequest } from 'ahooks';
import TableContainer, { ListEmptyState, renderDash } from '@/components/common/TableContainer';
import GenericFilterPopover from '@/components/common/GenericFilterPopover';
import type { FieldConfig, FilterValues } from '@/components/common/GenericFilterPopover';
import { HighlightText } from '@/components/common/HighlightText';
import { useListPage } from '@/hooks/useListPage';
import type { TableHookResult } from '@/hooks/useListPage';
import { extractApiError } from '@/hooks/useSalesOrders';
import apiClient from '@/services/api/axios.config';
import { clearCacheWhere } from '@/utils/cacheKeyRegistry';
import { useFormStyles } from '@/styles/formStyles';
import { controlHeightTokens, radiusTokens } from '@/styles/tokens';
import type {
  ApInvoiceStatus,
  ArInvoiceStatus,
  CreateVendorInvoiceRequest,
  CustomerInvoice,
  CustomerInvoiceDraft,
  GenerateCustomerInvoiceRequest,
  VendorInvoice,
} from '@/types/api/finance';
import type { PurchaseOrder } from '@/types/api/purchaseOrder';
import type { SalesOrder } from '@/types/api/salesOrder';
import { formatMoney } from '../salesOrders/components/soFormat';

const { Text } = Typography;

export type InvoiceKind = 'customer' | 'vendor';

const INVOICE_KINDS: readonly InvoiceKind[] = ['customer', 'vendor'] as const;

// ---------------------------------------------------------------------------
// Slice-local data seam (follow-up: lift into hooks/services — see docblock)
// ---------------------------------------------------------------------------

const CUSTOMER_INVOICES_ROUTE = '/finance/customer-invoices';
const VENDOR_INVOICES_ROUTE = '/finance/vendor-invoices';

const invoiceApi = {
  listCustomer: async (): Promise<{ data: CustomerInvoice[] }> => {
    const { data } = await apiClient.get<{ data: CustomerInvoice[] }>(CUSTOMER_INVOICES_ROUTE);
    return data;
  },
  listVendor: async (): Promise<{ data: VendorInvoice[] }> => {
    const { data } = await apiClient.get<{ data: VendorInvoice[] }>(VENDOR_INVOICES_ROUTE);
    return data;
  },
  draft: async (soId: string): Promise<{ data: CustomerInvoiceDraft }> => {
    const { data } = await apiClient.get<{ data: CustomerInvoiceDraft }>(
      `${CUSTOMER_INVOICES_ROUTE}/draft`,
      { params: { soId } }
    );
    return data;
  },
  generate: async (body: GenerateCustomerInvoiceRequest): Promise<{ data: CustomerInvoice }> => {
    const { data } = await apiClient.post<{ data: CustomerInvoice }>(
      `${CUSTOMER_INVOICES_ROUTE}/generate`,
      body
    );
    return data;
  },
  recordVendor: async (
    body: CreateVendorInvoiceRequest
  ): Promise<{ data: VendorInvoice }> => {
    const { data } = await apiClient.post<{ data: VendorInvoice }>(VENDOR_INVOICES_ROUTE, body);
    return data;
  },
};

/** Invalidate every cache an invoice write can stale (finance/OCC/SO/PO/GR). */
function invalidateInvoiceCaches() {
  clearCacheWhere(
    (key) =>
      key.startsWith('/api/finance') ||
      key.startsWith('/api/occ') ||
      key.startsWith('/api/sales-orders') ||
      key.startsWith('/api/purchase-orders') ||
      key.startsWith('/api/goods-receipts')
  );
}

// ---------------------------------------------------------------------------
// Normalized list row (one table, two record kinds)
// ---------------------------------------------------------------------------

interface InvoiceListRow {
  id: string;
  kind: InvoiceKind;
  invoiceNumber: string;
  dateLine: string | null;
  partyName: string;
  docId: string | null;
  docNumber: string | null;
  containerNumber: string | null;
  amount: number;
  paidAmount: number | null;
  currency: string;
  status: string;
  threeWayMatch: 'pass' | 'fail' | 'pending' | null;
  dueDate: string;
}

function toCustomerRow(invoice: CustomerInvoice): InvoiceListRow {
  return {
    id: invoice.id,
    kind: 'customer',
    invoiceNumber: invoice.invoiceNumber,
    dateLine: invoice.issuedAt ? invoice.issuedAt.slice(0, 10) : null,
    partyName: invoice.customerName,
    docId: invoice.soId ?? null,
    docNumber: invoice.soNumber ?? null,
    containerNumber: invoice.containerNumber ?? null,
    amount: invoice.amount,
    paidAmount: invoice.paidAmount,
    currency: invoice.currency,
    status: invoice.status,
    threeWayMatch: null,
    dueDate: invoice.dueDate,
  };
}

function toVendorRow(invoice: VendorInvoice): InvoiceListRow {
  return {
    id: invoice.id,
    kind: 'vendor',
    invoiceNumber: invoice.invoiceNumber,
    dateLine: invoice.invoiceDate,
    partyName: invoice.vendorName,
    docId: invoice.poId ?? null,
    docNumber: invoice.poNumber ?? null,
    containerNumber: null,
    amount: invoice.amount,
    paidAmount: null,
    currency: invoice.currency,
    status: invoice.status,
    threeWayMatch: invoice.threeWayMatch,
    dueDate: invoice.dueDate,
  };
}

interface InvoiceFilters {
  /** Route-derived — carried in filters so the table hook re-fetches per kind. */
  kind: InvoiceKind;
  search: string;
  status: string;
  [key: string]: unknown;
}

const defaultFilters = (kind: InvoiceKind): InvoiceFilters => ({
  kind,
  search: '',
  status: 'all',
});

const CUSTOMER_STATUSES: readonly ArInvoiceStatus[] = [
  'draft',
  'open',
  'partial',
  'paid',
  'void',
] as const;

const VENDOR_STATUSES: readonly ApInvoiceStatus[] = [
  'draft',
  'matched',
  'pending_approval',
  'approved',
  'paid',
  'held',
] as const;

/** antd Tag presets per status (house precedent; no colour literals). */
const CUSTOMER_STATUS_COLOR: Record<ArInvoiceStatus, string> = {
  draft: 'default',
  open: 'blue',
  partial: 'orange',
  paid: 'green',
  void: 'red',
};

const VENDOR_STATUS_COLOR: Record<ApInvoiceStatus, string> = {
  draft: 'default',
  matched: 'blue',
  pending_approval: 'orange',
  approved: 'cyan',
  paid: 'green',
  held: 'red',
};

const THREE_WAY_COLOR: Record<'pass' | 'fail' | 'pending', string> = {
  pass: 'green',
  fail: 'red',
  pending: 'orange',
};

/** House list contract: useAntdTable + pre-first-fetch guard, client filter at mock scale. */
function useInvoicesTable(filters: InvoiceFilters): TableHookResult<InvoiceListRow> {
  const { tableProps, refresh, loading, error, params } = useAntdTable(
    async ({ current, pageSize }) => {
      const rows =
        filters.kind === 'customer'
          ? (await invoiceApi.listCustomer()).data.map(toCustomerRow)
          : (await invoiceApi.listVendor()).data.map(toVendorRow);
      const term = filters.search.trim().toLowerCase();
      const filtered = rows.filter((row) => {
        if (filters.status !== 'all' && row.status !== filters.status) return false;
        if (!term) return true;
        return [row.invoiceNumber, row.partyName, row.docNumber ?? '', row.containerNumber ?? '']
          .join(' ')
          .toLowerCase()
          .includes(term);
      });
      const start = (current - 1) * pageSize;
      return { total: filtered.length, list: filtered.slice(start, start + pageSize) };
    },
    {
      defaultPageSize: 10,
      refreshDeps: [filters.kind, filters.search, filters.status],
    }
  );
  return {
    tableProps,
    isLoading: loading || params.length === 0,
    error: error ?? null,
    refresh,
  };
}

// ---------------------------------------------------------------------------
// Modal form schemas (zodResolver, mode onTouched — house contract)
// ---------------------------------------------------------------------------

const generateSchema = z.object({
  soId: z.string().min(1, 'finance:invoices.validation.soRequired'),
  invoiceNumber: z.string().min(1, 'finance:invoices.validation.invoiceNumberRequired'),
  dueDate: z.string(),
});

type GenerateFormData = z.infer<typeof generateSchema>;

const defaultGenerateValues: GenerateFormData = { soId: '', invoiceNumber: '', dueDate: '' };

const vendorSchema = z.object({
  poId: z.string().min(1, 'finance:invoices.validation.poRequired'),
  invoiceNumber: z.string().min(1, 'finance:invoices.validation.invoiceNumberRequired'),
  invoiceDate: z.string().min(1, 'finance:invoices.validation.invoiceDateRequired'),
  dueDate: z.string(),
  amount: z.number({ message: 'finance:invoices.validation.amountRequired' }).positive({
    message: 'finance:invoices.validation.amountRequired',
  }),
});

type VendorFormData = z.infer<typeof vendorSchema>;

const defaultVendorValues: VendorFormData = {
  poId: '',
  invoiceNumber: '',
  invoiceDate: dayjs().format('YYYY-MM-DD'),
  dueDate: '',
  amount: 0,
};

interface DraftLineState {
  fulfillmentLineId: string;
  sku: string;
  productName: string;
  qtyLoaded: number;
  qtyInvoiced: number;
  qtyOpenToInvoice: number;
  unitPrice: number;
  qty: number;
}

interface InvoiceFilterPopoverValues extends FilterValues {
  status?: string;
}

const InvoiceListPage = () => {
  const navigate = useNavigate();
  const { kind: kindParam } = useParams<{ kind: string }>();
  const { t } = useTranslation(['finance', 'common']);
  const { token } = theme.useToken();
  const { message } = App.useApp();
  const formStyles = useFormStyles();

  const kind = INVOICE_KINDS.find((candidate) => candidate === kindParam) ?? null;

  const [filters, setFilters] = useState<InvoiceFilters>(() => defaultFilters(kind ?? 'customer'));
  const [filterPopoverOpen, setFilterPopoverOpen] = useState(false);
  const [generateOpen, setGenerateOpen] = useState(false);
  const [vendorOpen, setVendorOpen] = useState(false);
  const [draftLines, setDraftLines] = useState<DraftLineState[]>([]);
  const [linesError, setLinesError] = useState<string | null>(null);

  // Route change = lens change: reset the filter state with it.
  useEffect(() => {
    setFilters(defaultFilters(kind ?? 'customer'));
  }, [kind]);

  const customFilterCounters = useMemo(
    () => ({ status: (value: unknown) => (value === 'all' ? 0 : 1) }),
    []
  );

  const { tableProps, isLoading, refresh, activeFilterCount, isFiltered, listState } = useListPage<
    InvoiceFilters,
    InvoiceListRow
  >({
    useTableHook: useInvoicesTable,
    filters,
    filterCountExclusions: ['search', 'kind'],
    customFilterCounters,
  });

  // SO options for the generate modal (mock-scale fetch, shared static cacheKey).
  const { data: soOptions, loading: soOptionsLoading } = useRequest(
    async () => {
      const { data } = await apiClient.get<{ data: SalesOrder[] }>('/sales-orders', {
        params: { page: 1, pageSize: 200 },
      });
      return data.data;
    },
    { cacheKey: '/api/sales-orders?view=invoice-options' }
  );

  // PO options for the vendor intake modal.
  const { data: poOptions, loading: poOptionsLoading } = useRequest(
    async () => {
      const { data } = await apiClient.get<{ data: PurchaseOrder[] }>('/purchase-orders', {
        params: { page: 1, pageSize: 200 },
      });
      return data.data;
    },
    { cacheKey: '/api/purchase-orders?view=invoice-options' }
  );

  const {
    control: generateControl,
    handleSubmit: handleGenerateSubmit,
    trigger: triggerGenerate,
    reset: resetGenerate,
    setValue: setGenerateValue,
    formState: { errors: generateErrors, isSubmitting: generating },
  } = useForm<GenerateFormData>({
    resolver: zodResolver(generateSchema),
    defaultValues: defaultGenerateValues,
    mode: 'onTouched',
    reValidateMode: 'onChange',
  });

  const {
    control: vendorControl,
    handleSubmit: handleVendorSubmit,
    trigger: triggerVendor,
    reset: resetVendor,
    watch: watchVendor,
    formState: { errors: vendorErrors, isSubmitting: recording },
  } = useForm<VendorFormData>({
    resolver: zodResolver(vendorSchema),
    defaultValues: defaultVendorValues,
    mode: 'onTouched',
    reValidateMode: 'onChange',
  });

  // Invoice-on-loaded draft — refetches per selected SO (manual run).
  const {
    data: draft,
    loading: draftLoading,
    run: loadDraft,
    mutate: setDraft,
  } = useRequest(async (soId: string) => (await invoiceApi.draft(soId)).data, {
    manual: true,
    onSuccess: (feed) => {
      setDraftLines(
        feed.lines.map((line) => ({
          fulfillmentLineId: line.fulfillmentLineId,
          sku: line.sku,
          productName: line.productName,
          qtyLoaded: line.qtyLoaded,
          qtyInvoiced: line.qtyInvoiced,
          qtyOpenToInvoice: line.qtyOpenToInvoice,
          unitPrice: line.unitPrice,
          qty: line.qtyOpenToInvoice,
        }))
      );
      setLinesError(null);
      if (feed.suggestedInvoiceNumber) {
        setGenerateValue('invoiceNumber', feed.suggestedInvoiceNumber);
        triggerGenerate('invoiceNumber');
      }
    },
  });

  const nothingToInvoice = !!draft && draftLines.length === 0;
  const invoiceTotal = draftLines.reduce(
    (sum, line) => sum + Math.round(line.qty * line.unitPrice * 100) / 100,
    0
  );

  const closeGenerate = () => {
    setGenerateOpen(false);
    resetGenerate(defaultGenerateValues);
    setDraft(undefined);
    setDraftLines([]);
    setLinesError(null);
  };

  const closeVendor = () => {
    setVendorOpen(false);
    resetVendor(defaultVendorValues);
  };

  const localizedInvoiceError = (body: { error?: string; message?: string }): string => {
    if (body.error === 'INVOICE_ABOVE_LOADED') return t('finance:invoices.errors.aboveLoaded');
    if (body.error === 'NOTHING_TO_INVOICE') return t('finance:invoices.errors.nothingToInvoice');
    if (body.error === 'DUPLICATE_INVOICE_NUMBER')
      return t('finance:invoices.errors.duplicateInvoice');
    return body.message || t('common:messages.createFailed');
  };

  const onGenerate = async (values: GenerateFormData) => {
    const lines = draftLines.filter((line) => line.qty > 0);
    if (lines.length === 0) {
      setLinesError(t('finance:invoices.validation.atLeastOne'));
      return;
    }
    try {
      await invoiceApi.generate({
        soId: values.soId,
        invoiceNumber: values.invoiceNumber.trim(),
        dueDate: values.dueDate || undefined,
        lines: lines.map((line) => ({ fulfillmentLineId: line.fulfillmentLineId, qty: line.qty })),
      });
      invalidateInvoiceCaches();
      message.success(t('finance:invoices.messages.generated'));
      closeGenerate();
      refresh();
    } catch (error) {
      message.error(localizedInvoiceError(extractApiError(error)));
    }
  };

  const onRecordVendor = async (values: VendorFormData) => {
    try {
      await invoiceApi.recordVendor({
        poId: values.poId,
        invoiceNumber: values.invoiceNumber.trim(),
        invoiceDate: values.invoiceDate,
        dueDate: values.dueDate || undefined,
        amount: values.amount,
      });
      invalidateInvoiceCaches();
      message.success(t('finance:invoices.messages.recorded'));
      closeVendor();
      refresh();
    } catch (error) {
      message.error(localizedInvoiceError(extractApiError(error)));
    }
  };

  const statusOptions = kind === 'vendor' ? VENDOR_STATUSES : CUSTOMER_STATUSES;
  const statusNs = kind === 'vendor' ? 'statusVendor' : 'statusCustomer';

  const filterFields: FieldConfig[] = useMemo(
    () => [
      {
        key: 'status',
        label: t('finance:invoices.fields.status'),
        type: 'radio-cards',
        defaultValue: 'all',
        options: [
          { value: 'all', label: t('finance:invoices.filters.allStatuses') },
          ...statusOptions.map((status) => ({
            value: status,
            label: t(`finance:invoices.${statusNs}.${status}`),
          })),
        ],
        resetText: t('common:actions.reset'),
      },
    ],
    [t, statusOptions, statusNs]
  );

  if (!kind) {
    return <Navigate to="/auth/finance/invoices/customer" replace />;
  }

  const generateFieldError = (key: keyof GenerateFormData) =>
    generateErrors[key] ? (
      <Text style={formStyles.errorStyle}>{t(generateErrors[key]?.message ?? '')}</Text>
    ) : null;

  const vendorFieldError = (key: keyof VendorFormData) =>
    vendorErrors[key] ? (
      <Text style={formStyles.errorStyle}>{t(vendorErrors[key]?.message ?? '')}</Text>
    ) : null;

  const selectedPo = poOptions?.find((po) => po.id === watchVendor('poId'));

  const columns: TableProps<InvoiceListRow>['columns'] = [
    {
      title: t('finance:invoices.fields.invoiceNumber'),
      dataIndex: 'invoiceNumber',
      key: 'invoiceNumber',
      width: 180,
      render: (_: string, record) => (
        <div className="tw-flex tw-flex-col tw-gap-0.5">
          <span style={{ fontWeight: token.fontWeightStrong }}>
            <HighlightText text={record.invoiceNumber} searchTerm={filters.search} fallback="-" />
          </span>
          {record.dateLine && (
            <span style={{ fontSize: token.fontSizeSM, color: token.colorTextSecondary }}>
              {record.dateLine}
            </span>
          )}
        </div>
      ),
    },
    {
      title:
        kind === 'customer'
          ? t('finance:invoices.fields.customer')
          : t('finance:invoices.fields.vendor'),
      dataIndex: 'partyName',
      key: 'partyName',
      width: 240,
      render: (value: string) => (
        <HighlightText text={value} searchTerm={filters.search} fallback="-" />
      ),
    },
    {
      title:
        kind === 'customer'
          ? t('finance:invoices.fields.salesOrder')
          : t('finance:invoices.fields.purchaseOrder'),
      dataIndex: 'docNumber',
      key: 'docNumber',
      width: 170,
      render: (value: string | null) =>
        value ? <HighlightText text={value} searchTerm={filters.search} fallback="-" /> : renderDash(),
    },
    {
      title: t('finance:invoices.fields.amount'),
      dataIndex: 'amount',
      key: 'amount',
      width: 150,
      align: 'right',
      render: (_: number, record) => (
        <span style={{ fontVariantNumeric: 'tabular-nums' }}>
          {formatMoney(record.currency, record.amount)}
        </span>
      ),
    },
    ...(kind === 'customer'
      ? [
          {
            title: t('finance:invoices.fields.outstanding'),
            key: 'outstanding',
            width: 150,
            align: 'right' as const,
            render: (_: unknown, record: InvoiceListRow) => (
              <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                {formatMoney(
                  record.currency,
                  Math.round((record.amount - (record.paidAmount ?? 0)) * 100) / 100
                )}
              </span>
            ),
          },
        ]
      : [
          {
            title: t('finance:invoices.fields.threeWay'),
            dataIndex: 'threeWayMatch',
            key: 'threeWayMatch',
            width: 120,
            render: (value: InvoiceListRow['threeWayMatch']) =>
              value ? (
                <Tag color={THREE_WAY_COLOR[value]} style={{ margin: 0 }}>
                  {t(`finance:invoices.threeWay.${value}`)}
                </Tag>
              ) : (
                renderDash()
              ),
          },
        ]),
    {
      title: t('finance:invoices.fields.dueDate'),
      dataIndex: 'dueDate',
      key: 'dueDate',
      width: 120,
      render: (value: string) => value || renderDash(),
    },
    {
      title: t('finance:invoices.fields.status'),
      dataIndex: 'status',
      key: 'status',
      width: 130,
      render: (status: string) => (
        <Tag
          color={
            kind === 'customer'
              ? CUSTOMER_STATUS_COLOR[status as ArInvoiceStatus]
              : VENDOR_STATUS_COLOR[status as ApInvoiceStatus]
          }
          style={{ margin: 0 }}
        >
          {t(`finance:invoices.${statusNs}.${status}`)}
        </Tag>
      ),
    },
    {
      title: t('finance:invoices.fields.actions'),
      key: 'actions',
      width: 80,
      fixed: 'right',
      render: (_: unknown, record) => (
        <Dropdown
          menu={{
            items: [
              {
                key: 'view',
                label: t('finance:invoices.actions.view'),
                icon: <FileTextOutlined />,
                onClick: (e) => {
                  e.domEvent.stopPropagation();
                  navigate(`/auth/finance/invoices/${kind}/${record.id}`);
                },
              },
              ...(record.docId
                ? [
                    {
                      key: 'viewDoc',
                      label:
                        kind === 'customer'
                          ? t('finance:invoices.actions.viewSo')
                          : t('finance:invoices.actions.viewPo'),
                      icon: <EyeOutlined />,
                      onClick: (e: { domEvent: { stopPropagation: () => void } }) => {
                        e.domEvent.stopPropagation();
                        navigate(
                          kind === 'customer'
                            ? `/auth/sales-orders/${record.docId}`
                            : `/auth/purchase-orders/${record.docId}`
                        );
                      },
                    },
                  ]
                : []),
            ],
          }}
          trigger={['click']}
        >
          <Button
            type="text"
            aria-label={t('finance:invoices.actions.actionsFor', {
              invoiceNumber: record.invoiceNumber,
            })}
            icon={<MoreOutlined />}
            onClick={(e) => e.stopPropagation()}
          />
        </Dropdown>
      ),
    },
  ];

  return (
    <div className="tw-px-6 tw-pb-8">
      <div className="tw-mb-6">
        <h1 style={{ fontSize: token.fontSizeHeading2, fontWeight: token.fontWeightStrong }}>
          {t('finance:invoices.list.title')}
        </h1>
      </div>

      {/* Route-derived kind tabs — selection lives in the URL */}
      <Tabs
        activeKey={kind}
        onChange={(key) => navigate(`/auth/finance/invoices/${key}`)}
        items={[
          { key: 'customer', label: t('finance:invoices.tabs.customer') },
          { key: 'vendor', label: t('finance:invoices.tabs.vendor') },
        ]}
      />

      {/* Search + status filter + create toolbar */}
      <div className="tw-mb-6 tw-flex tw-gap-3 tw-items-center">
        <Input
          placeholder={t(`finance:invoices.searchPlaceholder.${kind}`)}
          value={filters.search}
          onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))}
          variant="outlined"
          size="large"
          suffix={<SearchOutlined style={{ fontSize: token.sizeMD, color: token.colorPrimary }} />}
          style={{
            width: '450px',
            borderRadius: radiusTokens.card,
            height: `${controlHeightTokens.controlHeightXL}px`,
          }}
        />
        <GenericFilterPopover<InvoiceFilterPopoverValues>
          open={filterPopoverOpen}
          onOpenChange={setFilterPopoverOpen}
          onApply={(values) =>
            setFilters((prev) => ({ ...prev, status: (values.status as string) || 'all' }))
          }
          initialFilters={{ status: filters.status }}
          fields={filterFields}
          applyText={t('common:actions.apply')}
          cancelText={t('common:actions.cancel')}
        >
          <Badge count={activeFilterCount}>
            <Button
              aria-label={t('common:actions.filter')}
              icon={<FilterOutlined style={{ color: token.colorPrimary }} />}
              size="large"
              style={{
                borderRadius: radiusTokens.card,
                height: `${controlHeightTokens.controlHeightXL}px`,
                width: `${controlHeightTokens.controlHeightXL}px`,
              }}
            />
          </Badge>
        </GenericFilterPopover>
        <div className="tw-flex tw-gap-3" style={{ marginLeft: 'auto' }}>
          {kind === 'customer' ? (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              size="large"
              onClick={() => setGenerateOpen(true)}
              style={{
                borderRadius: radiusTokens.card,
                height: `${controlHeightTokens.controlHeightXL}px`,
                paddingLeft: token.paddingLG,
                paddingRight: token.paddingLG,
              }}
            >
              {t('finance:invoices.actions.generate')}
            </Button>
          ) : (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              size="large"
              onClick={() => setVendorOpen(true)}
              style={{
                borderRadius: radiusTokens.card,
                height: `${controlHeightTokens.controlHeightXL}px`,
                paddingLeft: token.paddingLG,
                paddingRight: token.paddingLG,
              }}
            >
              {t('finance:invoices.actions.record')}
            </Button>
          )}
        </div>
      </div>

      {/* Table — S0.3 state triad via TableContainer + ListEmptyState */}
      <TableContainer
        errorState={{
          active: listState === 'error',
          title: t('finance:invoices.states.loadFailed'),
          retryLabel: t('common:errors.boundary.retry'),
          onRetry: refresh,
        }}
      >
        <Table<InvoiceListRow>
          {...tableProps}
          columns={columns}
          rowKey="id"
          loading={isLoading}
          onRow={(record) => ({
            onClick: () => navigate(`/auth/finance/invoices/${kind}/${record.id}`),
            style: { cursor: 'pointer' },
          })}
          locale={{
            emptyText: isFiltered ? (
              <ListEmptyState
                title={t('finance:invoices.states.noResults')}
                actionLabel={t('finance:invoices.states.clearFilters')}
                onAction={() => setFilters(defaultFilters(kind))}
                actionType="default"
              />
            ) : (
              <ListEmptyState
                title={
                  kind === 'customer'
                    ? t('finance:invoices.states.noCustomerInvoices')
                    : t('finance:invoices.states.noVendorInvoices')
                }
                actionLabel={
                  kind === 'customer'
                    ? t('finance:invoices.actions.generate')
                    : t('finance:invoices.actions.record')
                }
                onAction={() => (kind === 'customer' ? setGenerateOpen(true) : setVendorOpen(true))}
                actionIcon={<PlusOutlined />}
              />
            ),
          }}
          pagination={{ ...tableProps.pagination, showSizeChanger: false }}
          scroll={{ x: 1150 }}
        />
      </TableContainer>

      {/* 產生客戶發票 — generated from loaded qty ONLY (invoice-on-loaded) */}
      <Modal
        open={generateOpen}
        title={t('finance:invoices.generateModal.title')}
        onCancel={closeGenerate}
        width={800}
        footer={[
          <Button
            key="cancel"
            size="large"
            style={{ height: `${controlHeightTokens.controlHeightXL}px` }}
            onClick={closeGenerate}
          >
            {t('common:actions.cancel')}
          </Button>,
          // Nothing invoiceable → the submit action is HIDDEN (hide-don't-disable)
          ...(nothingToInvoice
            ? []
            : [
                <Button
                  key="generate"
                  type="primary"
                  size="large"
                  style={{ height: `${controlHeightTokens.controlHeightXL}px` }}
                  loading={generating}
                  onClick={() => void handleGenerateSubmit(onGenerate)()}
                >
                  {t('finance:invoices.generateModal.submit')}
                </Button>,
              ]),
        ]}
      >
        <div style={{ ...formStyles.fieldContainerStyle, marginBottom: '32px', padding: '20px' }}>
          <Text strong style={formStyles.sectionTitleStyle}>
            {t('finance:invoices.generateModal.sourceSection')}
          </Text>
          <Row gutter={[16, 16]}>
            <Col span={24}>
              <Text style={formStyles.labelStyle}>
                <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
                {t('finance:invoices.generateModal.soLabel')}
              </Text>
              <Controller
                name="soId"
                control={generateControl}
                render={({ field }) => (
                  <Select
                    value={field.value || undefined}
                    onChange={(value) => {
                      field.onChange(value ?? '');
                      triggerGenerate('soId');
                      if (value) loadDraft(value);
                    }}
                    onBlur={field.onBlur}
                    options={(soOptions ?? []).map((so) => ({
                      value: so.id,
                      label: `${so.soNumber} — ${so.customerName}`,
                    }))}
                    loading={soOptionsLoading}
                    showSearch
                    optionFilterProp="label"
                    placeholder={t('finance:invoices.generateModal.selectSoPlaceholder')}
                    size="large"
                    style={formStyles.inputStyle}
                    status={generateErrors.soId ? 'error' : undefined}
                  />
                )}
              />
              {generateFieldError('soId')}
            </Col>
          </Row>
          {nothingToInvoice && (
            <Alert
              className="tw-mt-3"
              type="info"
              showIcon
              message={t('finance:invoices.generateModal.nothingTitle')}
              description={t('finance:invoices.generateModal.nothingHint')}
            />
          )}
        </div>

        {draft && !nothingToInvoice && (
          <>
            <div
              style={{ ...formStyles.fieldContainerStyle, marginBottom: '32px', padding: '20px' }}
            >
              <Text strong style={formStyles.sectionTitleStyle}>
                {t('finance:invoices.generateModal.headerSection')}
              </Text>
              <Row gutter={[16, 16]}>
                <Col span={12}>
                  <Text style={formStyles.labelStyle}>
                    <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
                    {t('finance:invoices.generateModal.invoiceNumberLabel')}
                  </Text>
                  <Controller
                    name="invoiceNumber"
                    control={generateControl}
                    render={({ field }) => (
                      <Input
                        {...field}
                        placeholder={t('finance:invoices.generateModal.invoiceNumberHint')}
                        size="large"
                        style={formStyles.inputStyle}
                        status={generateErrors.invoiceNumber ? 'error' : undefined}
                      />
                    )}
                  />
                  <Text
                    type="secondary"
                    style={{ display: 'block', fontSize: token.fontSizeSM, marginTop: '4px' }}
                  >
                    {t('finance:invoices.generateModal.invoiceNumberHint')}
                  </Text>
                  {generateFieldError('invoiceNumber')}
                </Col>
                <Col span={12}>
                  <Text style={formStyles.labelStyle}>
                    {t('finance:invoices.generateModal.dueDateLabel')}
                  </Text>
                  <Controller
                    name="dueDate"
                    control={generateControl}
                    render={({ field }) => (
                      <DatePicker
                        value={field.value ? dayjs(field.value) : null}
                        onChange={(value) =>
                          field.onChange(value ? value.format('YYYY-MM-DD') : '')
                        }
                        onBlur={field.onBlur}
                        size="large"
                        style={formStyles.inputStyle}
                      />
                    )}
                  />
                </Col>
              </Row>
            </div>

            <div style={{ ...formStyles.fieldContainerStyle, padding: '20px' }}>
              <Text strong style={formStyles.sectionTitleStyle}>
                {t('finance:invoices.generateModal.linesSection')}
              </Text>
              <Text
                type="secondary"
                style={{ display: 'block', fontSize: token.fontSizeSM, marginBottom: '12px' }}
              >
                {t('finance:invoices.generateModal.loadedBasisHint')}
              </Text>
              <Table<DraftLineState>
                size="small"
                rowKey="fulfillmentLineId"
                loading={draftLoading}
                dataSource={draftLines}
                pagination={false}
                columns={[
                  {
                    title: t('finance:invoices.fields.product'),
                    key: 'product',
                    render: (_: unknown, line) => (
                      <div className="tw-flex tw-flex-col tw-gap-0.5">
                        <span style={{ fontWeight: token.fontWeightStrong }}>{line.sku}</span>
                        <span
                          style={{ fontSize: token.fontSizeSM, color: token.colorTextSecondary }}
                        >
                          {line.productName}
                        </span>
                      </div>
                    ),
                  },
                  {
                    title: t('finance:invoices.fields.qtyLoaded'),
                    dataIndex: 'qtyLoaded',
                    key: 'qtyLoaded',
                    align: 'right',
                    render: (value: number) => (
                      <span style={{ fontVariantNumeric: 'tabular-nums' }}>{value}</span>
                    ),
                  },
                  {
                    title: t('finance:invoices.fields.qtyInvoiced'),
                    dataIndex: 'qtyInvoiced',
                    key: 'qtyInvoiced',
                    align: 'right',
                    render: (value: number) => (
                      <span style={{ fontVariantNumeric: 'tabular-nums' }}>{value}</span>
                    ),
                  },
                  {
                    title: t('finance:invoices.fields.qtyOpenToInvoice'),
                    dataIndex: 'qtyOpenToInvoice',
                    key: 'qtyOpenToInvoice',
                    align: 'right',
                    render: (value: number) => (
                      <span style={{ fontVariantNumeric: 'tabular-nums' }}>{value}</span>
                    ),
                  },
                  {
                    title: t('finance:invoices.fields.qtyToInvoice'),
                    key: 'qty',
                    width: 140,
                    render: (_: unknown, line) => (
                      <InputNumber
                        min={0}
                        max={line.qtyOpenToInvoice}
                        value={line.qty}
                        size="large"
                        style={{ width: '100%' }}
                        aria-label={t('finance:invoices.fields.qtyToInvoice')}
                        onChange={(value) => {
                          setLinesError(null);
                          setDraftLines((prev) =>
                            prev.map((candidate) =>
                              candidate.fulfillmentLineId === line.fulfillmentLineId
                                ? { ...candidate, qty: value ?? 0 }
                                : candidate
                            )
                          );
                        }}
                      />
                    ),
                  },
                  {
                    title: t('finance:invoices.fields.unitPrice'),
                    dataIndex: 'unitPrice',
                    key: 'unitPrice',
                    align: 'right',
                    render: (value: number) => (
                      <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                        {formatMoney(draft.currency, value)}
                      </span>
                    ),
                  },
                ]}
              />
              <div className="tw-mt-3 tw-flex tw-justify-end">
                <Text strong>
                  {t('finance:invoices.generateModal.totalLabel')}
                  {': '}
                  <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {formatMoney(draft.currency, Math.round(invoiceTotal * 100) / 100)}
                  </span>
                </Text>
              </div>
              {linesError && <Text style={formStyles.errorStyle}>{linesError}</Text>}
            </div>
          </>
        )}
      </Modal>

      {/* 登錄供應商發票 — manual intake MVP (未整合) */}
      <Modal
        open={vendorOpen}
        title={t('finance:invoices.vendorModal.title')}
        onCancel={closeVendor}
        width={800}
        footer={[
          <Button
            key="cancel"
            size="large"
            style={{ height: `${controlHeightTokens.controlHeightXL}px` }}
            onClick={closeVendor}
          >
            {t('common:actions.cancel')}
          </Button>,
          <Button
            key="record"
            type="primary"
            size="large"
            style={{ height: `${controlHeightTokens.controlHeightXL}px` }}
            loading={recording}
            onClick={() => void handleVendorSubmit(onRecordVendor)()}
          >
            {t('finance:invoices.vendorModal.submit')}
          </Button>,
        ]}
      >
        <div style={{ ...formStyles.fieldContainerStyle, marginBottom: '32px', padding: '20px' }}>
          <Text strong style={formStyles.sectionTitleStyle}>
            {t('finance:invoices.vendorModal.sourceSection')}
          </Text>
          <Row gutter={[16, 16]}>
            <Col span={24}>
              <Text style={formStyles.labelStyle}>
                <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
                {t('finance:invoices.vendorModal.poLabel')}
              </Text>
              <Controller
                name="poId"
                control={vendorControl}
                render={({ field }) => (
                  <Select
                    value={field.value || undefined}
                    onChange={(value) => {
                      field.onChange(value ?? '');
                      triggerVendor('poId');
                    }}
                    onBlur={field.onBlur}
                    options={(poOptions ?? []).map((po) => ({
                      value: po.id,
                      label: `${po.poNumber} — ${po.vendorName}`,
                    }))}
                    loading={poOptionsLoading}
                    showSearch
                    optionFilterProp="label"
                    placeholder={t('finance:invoices.vendorModal.selectPoPlaceholder')}
                    size="large"
                    style={formStyles.inputStyle}
                    status={vendorErrors.poId ? 'error' : undefined}
                  />
                )}
              />
              {vendorFieldError('poId')}
            </Col>
          </Row>
          <Alert
            className="tw-mt-3"
            type="info"
            showIcon
            message={t('finance:invoices.vendorModal.manualHint')}
          />
        </div>

        <div style={{ ...formStyles.fieldContainerStyle, padding: '20px' }}>
          <Text strong style={formStyles.sectionTitleStyle}>
            {t('finance:invoices.vendorModal.detailSection')}
          </Text>
          <Row gutter={[16, 16]}>
            <Col span={12}>
              <Text style={formStyles.labelStyle}>
                <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
                {t('finance:invoices.vendorModal.invoiceNumberLabel')}
              </Text>
              <Controller
                name="invoiceNumber"
                control={vendorControl}
                render={({ field }) => (
                  <Input
                    {...field}
                    size="large"
                    style={formStyles.inputStyle}
                    status={vendorErrors.invoiceNumber ? 'error' : undefined}
                  />
                )}
              />
              {vendorFieldError('invoiceNumber')}
            </Col>
            <Col span={12}>
              <Text style={formStyles.labelStyle}>
                <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
                {t('finance:invoices.vendorModal.invoiceDateLabel')}
              </Text>
              <Controller
                name="invoiceDate"
                control={vendorControl}
                render={({ field }) => (
                  <DatePicker
                    value={field.value ? dayjs(field.value) : null}
                    onChange={(value) => {
                      field.onChange(value ? value.format('YYYY-MM-DD') : '');
                      triggerVendor('invoiceDate');
                    }}
                    onBlur={field.onBlur}
                    size="large"
                    style={formStyles.inputStyle}
                    status={vendorErrors.invoiceDate ? 'error' : undefined}
                  />
                )}
              />
              {vendorFieldError('invoiceDate')}
            </Col>
          </Row>
          <Row gutter={[16, 16]} style={{ marginTop: '12px' }}>
            <Col span={12}>
              <Text style={formStyles.labelStyle}>
                <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
                {t('finance:invoices.vendorModal.amountLabel')}
                {selectedPo ? ` (${selectedPo.currency})` : ''}
              </Text>
              <Controller
                name="amount"
                control={vendorControl}
                render={({ field }) => (
                  <InputNumber
                    value={field.value}
                    onChange={(value) => {
                      field.onChange(value ?? 0);
                      triggerVendor('amount');
                    }}
                    onBlur={field.onBlur}
                    min={0}
                    size="large"
                    style={{ ...formStyles.inputStyle, width: '100%' }}
                    status={vendorErrors.amount ? 'error' : undefined}
                  />
                )}
              />
              {vendorFieldError('amount')}
            </Col>
            <Col span={12}>
              <Text style={formStyles.labelStyle}>
                {t('finance:invoices.vendorModal.dueDateLabel')}
              </Text>
              <Controller
                name="dueDate"
                control={vendorControl}
                render={({ field }) => (
                  <DatePicker
                    value={field.value ? dayjs(field.value) : null}
                    onChange={(value) => field.onChange(value ? value.format('YYYY-MM-DD') : '')}
                    onBlur={field.onBlur}
                    size="large"
                    style={formStyles.inputStyle}
                  />
                )}
              />
            </Col>
          </Row>
        </div>
      </Modal>
    </div>
  );
};

export default InvoiceListPage;
